import { useTranslation } from 'react-i18next'
import { useState, useMemo, useCallback } from 'react'
import {
  ArrayParam,
  BooleanParam,
  NumberParam,
  QueryParamConfig,
  StringParam,
  useQueryParams,
  withDefault
} from 'use-query-params'

import { useBreadcrumbs } from '../../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'
import { RoutePath } from '../../layout/Routes'
import { useProgramStates } from '../hooks/useProgramStates/useProgramStates'
import { ContentOrError } from '../shared/somethingWentWrong/SomethingWentWrong'

import { useDidMountEffect, useQuery, useQueryList, useRequiredParams } from '@percent/partner-dashboard/common/hooks'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { MountableTab as Tab, TableColumnSortDirection } from '@percent/lemonade'
import {
  CurrentStateDB,
  GetCandidatesSortBy,
  ProgramStateOption
} from '@percent/partner-dashboard/services/grants/grantsService.types'
import { CandidatesQueryParams } from './CandidatesContainer.types'
import { CandidatesContextController } from './candidatesContext/CandidatesContextController'
import { AllCandidatesTable } from './allCandidatesTable/AllCandidatesTable'
import { StagesTabs } from './stagesTabs/StagesTabs'
import { TaskCandidatesTable } from './taskCandidatesTable/TaskCandidatesTable'
import { RejectedCandidatesTable } from './rejectedCandidatesTable/RejectedCandidatesTable'
import { DisbursementsTable } from './disbursementsTable/DisbursementsTable'
import { MultipleTasksCandidatesTable } from './multipleTasksCandidatesTable/MultipleTasksCandidatesTable'
import { useCandidatesEffectiveParams } from './hooks/useCandidatesEffectiveParams'

const CANDIDATES_QUERY_PAGE_SIZE = 100
const initialDirection: TableColumnSortDirection = 'DESC'
export const initialActiveTabLabel = 'All' as const

export function CandidatesContainer() {
  const { t } = useTranslation()
  const [queryParams, setQueryParams] = useQueryParams<CandidatesQueryParams>({
    activeTab: withDefault(StringParam, initialActiveTabLabel),
    direction: StringParam as QueryParamConfig<TableColumnSortDirection | null | undefined>,
    sortBy: StringParam as QueryParamConfig<GetCandidatesSortBy | null | undefined>,
    scoreName: StringParam,
    scoreMin: NumberParam,
    scoreMax: NumberParam,
    searchTerm: StringParam,
    currentState: ArrayParam as QueryParamConfig<CurrentStateDB[] | null | undefined>,
    assigneeEmails: ArrayParam as QueryParamConfig<string[] | null | undefined>,
    includeUnassigned: BooleanParam
  })

  const [direction, setDirection] = useState<TableColumnSortDirection>(
    (queryParams.direction as TableColumnSortDirection) ?? initialDirection
  )

  const { id: programId } = useRequiredParams()
  const { groupedStates, allStates, tabs, getStateIdsForGroup, getTaskTemplatesForGroup } = useProgramStates({
    programId
  })
  const { grantsService } = useServices()

  const [{ data: program }] = useQuery(grantsService.getProgram, { programId })
  const programName = program?.name ?? ''

  useBreadcrumbs([
    {
      label: t('breadcrumb.grants'),
      path: RoutePath.GRANT_PROGRAMS
    },
    { label: programName }
  ])

  const effectiveParams = useCandidatesEffectiveParams({
    programId,
    pageSize: CANDIDATES_QUERY_PAGE_SIZE,
    queryParams
  })

  const [
    { data: candidates, isLoading: isLoadingCandidates, totalResults: candidatesResults, errorMessage },
    { query, nextPage, previousPage, refresh }
  ] = useQueryList(grantsService.getCandidates, effectiveParams)

  useDidMountEffect(() => {
    query(effectiveParams)
  }, [programId, query, effectiveParams])

  const handleRequestSort = (columnKey: string, options?: { scoreName?: string }) => {
    const newDirection = direction === 'ASC' ? 'DESC' : 'ASC'

    setDirection(newDirection)
    // scoreName is left untouched for other columns — an active score range filter still needs it
    setQueryParams({
      direction: newDirection,
      sortBy: columnKey,
      ...(options?.scoreName ? { scoreName: options.scoreName } : {})
    })
  }

  const { activeTab } = queryParams

  const selectedActiveTab = useMemo(() => {
    const matchingTab = tabs.find(tab => tab.label === activeTab)

    return matchingTab?.key ?? CurrentStateDB.ALL
  }, [activeTab, tabs])

  const selectActiveTab = useCallback(
    (key: string) => {
      const matchingTab = tabs.find(tab => tab.key === key)

      if (!matchingTab) return

      if (key === CurrentStateDB.ALL) {
        setQueryParams({
          activeTab: matchingTab.label,
          currentState: undefined,
          sortBy: undefined,
          scoreName: undefined,
          scoreMin: undefined,
          scoreMax: undefined
        })

        return
      }

      const currentStates = getStateIdsForGroup(key)
      setQueryParams({
        activeTab: matchingTab.label,
        currentState: currentStates,
        sortBy: undefined,
        scoreName: undefined,
        scoreMin: undefined,
        scoreMax: undefined
      })
    },
    [tabs, setQueryParams, getStateIdsForGroup]
  )

  const renderTableForState = (state: ProgramStateOption) => {
    switch (state.tableType) {
      case 'rejected':
        return <RejectedCandidatesTable />
      case 'status':
        return (
          <AllCandidatesTable enabledFilters={{ organisationSearch: true, currentState: false }} validStates={[]} />
        )
      case 'task':
        return <TaskCandidatesTable taskTemplates={getTaskTemplatesForGroup(state.key)} />
      case 'task_with_score':
        return <TaskCandidatesTable taskTemplates={getTaskTemplatesForGroup(state.key)} scoreName={state.scoreName} />
      case 'multiple_tasks':
        return <MultipleTasksCandidatesTable taskTemplates={getTaskTemplatesForGroup(state.key)} />
      case 'disbursement':
        return <DisbursementsTable />
      default:
        return <TaskCandidatesTable taskTemplates={[]} />
    }
  }

  return (
    <CandidatesContextController
      program={{ id: programId, name: programName, entryType: program?.entryType }}
      queryParams={queryParams}
      setQueryParams={setQueryParams}
      sort={{ direction, handleRequestSort }}
      candidates={{
        data: candidates ?? [],
        isLoading: isLoadingCandidates,
        totalResults: candidatesResults ?? 0,
        previousPage,
        nextPage,
        refresh
      }}
    >
      <StagesTabs activeTab={selectedActiveTab} setActiveTab={selectActiveTab} tabs={tabs} />
      <Tab value={selectedActiveTab} index={tabs[0].key}>
        <ContentOrError error={errorMessage}>
          <AllCandidatesTable
            enabledFilters={{ organisationSearch: true, currentState: true }}
            validStates={allStates}
          />
        </ContentOrError>
      </Tab>
      {groupedStates.map(state => (
        <Tab key={state.key} value={selectedActiveTab} index={state.key}>
          <ContentOrError error={errorMessage}>{renderTableForState(state)}</ContentOrError>
        </Tab>
      ))}
    </CandidatesContextController>
  )
}
